/**
 * Dashboard forensique — Audit des téléchargements de sujets d'examen.
 *
 * Réservé aux super_admin. Alimenté par LogAudit (branché sur
 * `telecharger_sujet_depot` depuis le 2026-08-27 après la fuite du 17/08).
 *
 * L'endpoint back retourne :
 *   - items : lignes de log (date, user, ip, ua, dépôt, détail JSON)
 *   - top_users : classement par nb de téléchargements sur la fenêtre
 *   - top_depots : sujets les plus téléchargés sur la fenêtre
 */
import React, { useMemo, useState } from 'react';
import { Shield, Download, Filter, RefreshCw, AlertTriangle, ChevronDown, User, Clock, Globe } from 'lucide-react';
import {
  useAuditTelechargementsSujetsQuery,
  AuditTelechargementLigne,
} from '../../services/formateurApi';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';

const AuditSujets: React.FC = () => {
  const user = useSelector((s: RootState) => s.auth.user);

  // Filtres — par défaut 30 derniers jours (aligné sur le backend)
  const now = new Date();
  const in30j = new Date(now.getTime() - 30 * 24 * 3600 * 1000);
  const iso = (d: Date) => d.toISOString().slice(0, 10);

  const [depuis, setDepuis] = useState<string>(iso(in30j));
  const [jusqu, setJusqu] = useState<string>(iso(now));
  const [userIdFilter, setUserIdFilter] = useState<string>('');
  const [depotIdFilter, setDepotIdFilter] = useState<string>('');
  const [limit, setLimit] = useState<number>(200);
  const [detailOpen, setDetailOpen] = useState<Record<number, boolean>>({});
  const [uaOpen, setUaOpen] = useState<Record<number, boolean>>({});

  const { data, isFetching, refetch, error } = useAuditTelechargementsSujetsQuery(
    {
      depuis: depuis || undefined,
      jusqu: jusqu || undefined,
      user_id: userIdFilter ? Number(userIdFilter) : undefined,
      depot_id: depotIdFilter ? Number(depotIdFilter) : undefined,
      limit,
    },
    { pollingInterval: 60_000 },
  );

  const isSuperAdmin = user?.role === 'super_admin';
  const items = data?.items || [];
  const topUsers = data?.top_users || [];
  const topDepots = data?.top_depots || [];
  const total = data?.total ?? 0;

  // Détecte les téléchargements « nocturnes » (22h-06h) — indicateur d'usage suspect
  const isNightRow = (iso: string): boolean => {
    try {
      const h = new Date(iso).getHours();
      return h >= 22 || h < 6;
    } catch { return false; }
  };

  const badgeMobile = (ua: string): string | null => {
    const u = (ua || '').toLowerCase();
    if (/android|iphone|ipad|mobile|blackberry|opera mini/.test(u)) return 'Mobile';
    return null;
  };

  const parseDetail = (raw: string): Record<string, any> | null => {
    try { return JSON.parse(raw); } catch { return null; }
  };

  const exportCSV = () => {
    if (!items.length) return;
    const header = ['date', 'user_id', 'user', 'role', 'depot_id', 'ip', 'nuit', 'ua', 'detail'];
    const rows = items.map(l => [
      l.date,
      String(l.user_id ?? ''),
      `"${(l.user_nom || '').replace(/"/g, '""')}" (${l.user_username})`,
      l.user_role,
      String(l.depot_id ?? ''),
      l.ip,
      isNightRow(l.date) ? '1' : '0',
      `"${(l.user_agent || '').replace(/"/g, '""')}"`,
      `"${(l.detail || '').replace(/"/g, '""')}"`,
    ].join(','));
    const blob = new Blob(['﻿' + [header.join(','), ...rows].join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit_sujets_${depuis}_${jusqu}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const nbNuit = useMemo(
    () => items.filter(l => isNightRow(l.date)).length,
    [items],
  );

  if (!isSuperAdmin) {
    return (
      <div className="p-8">
        <div className="max-w-xl mx-auto bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6 text-red-800 dark:text-red-300">
          <div className="flex items-center gap-2 mb-2 font-semibold">
            <Shield size={20} /> Accès refusé
          </div>
          <div className="text-sm">
            Ce dashboard forensique est réservé aux super-administrateurs.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-5">
      {/* En-tête */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Shield className="text-red-500" size={24} /> Audit — Téléchargements de sujets
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Chaque accès à un dépôt de sujet d'examen est journalisé (utilisateur, IP,
            user-agent, détail). Fenêtre par défaut : 30 derniers jours.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="inline-flex items-center gap-1 px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
            Rafraîchir
          </button>
          <button
            onClick={exportCSV}
            disabled={!items.length}
            className="inline-flex items-center gap-1 px-3 py-2 text-sm rounded-lg border border-amber-300 bg-amber-500 hover:bg-amber-600 text-white disabled:opacity-50"
          >
            <Download size={14} /> CSV
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="Total téléchargements" value={total} tone="neutral" />
        <KpiCard label="Lignes affichées" value={items.length} tone="neutral" />
        <KpiCard label="Utilisateurs distincts" value={topUsers.length} tone="neutral" />
        <KpiCard
          label="Nocturnes (22h-06h)"
          value={nbNuit}
          tone={nbNuit > 0 ? 'warn' : 'neutral'}
          hint={nbNuit > 0 ? 'Investiguer' : undefined}
        />
      </div>

      {/* Filtres */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center gap-2 mb-3 text-sm font-medium text-gray-700 dark:text-gray-300">
          <Filter size={14} /> Filtres
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <FilterField label="Depuis">
            <input type="date" value={depuis} onChange={(e) => setDepuis(e.target.value)}
              className="w-full px-2 py-1.5 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100" />
          </FilterField>
          <FilterField label="Jusqu'au">
            <input type="date" value={jusqu} onChange={(e) => setJusqu(e.target.value)}
              className="w-full px-2 py-1.5 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100" />
          </FilterField>
          <FilterField label="ID utilisateur">
            <input type="number" min={1} value={userIdFilter} onChange={(e) => setUserIdFilter(e.target.value)}
              placeholder="tous"
              className="w-full px-2 py-1.5 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100" />
          </FilterField>
          <FilterField label="ID dépôt">
            <input type="number" min={1} value={depotIdFilter} onChange={(e) => setDepotIdFilter(e.target.value)}
              placeholder="tous"
              className="w-full px-2 py-1.5 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100" />
          </FilterField>
          <FilterField label="Limite">
            <select value={limit} onChange={(e) => setLimit(Number(e.target.value))}
              className="w-full px-2 py-1.5 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100">
              {[100, 200, 500, 1000].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </FilterField>
        </div>
      </div>

      {error && (
        <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-sm">
          Erreur au chargement — vérifie tes droits (super_admin uniquement).
        </div>
      )}

      {/* Classements côte à côte */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center gap-2 mb-3">
            <User size={16} className="text-amber-500" />
            <div className="font-semibold text-gray-900 dark:text-gray-100">Top utilisateurs</div>
          </div>
          {topUsers.length === 0 && (
            <div className="text-sm text-gray-500 dark:text-gray-400">Aucun téléchargement sur la période.</div>
          )}
          <div className="space-y-1">
            {topUsers.map((u, i) => (
              <button
                key={i}
                onClick={() => setUserIdFilter(String(u.user_id ?? ''))}
                className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/40 text-left"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                    {u.nom_complet}
                    <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
                      {u.username} · {u.role}
                    </span>
                  </div>
                </div>
                <div className={`shrink-0 px-2 py-0.5 rounded text-xs font-semibold ${
                  u.nb_telechargements >= 20 ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                  : u.nb_telechargements >= 5 ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                  : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200'
                }`}>
                  {u.nb_telechargements}
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle size={16} className="text-red-500" />
            <div className="font-semibold text-gray-900 dark:text-gray-100">Sujets les plus téléchargés</div>
          </div>
          {topDepots.length === 0 && (
            <div className="text-sm text-gray-500 dark:text-gray-400">Aucun dépôt téléchargé.</div>
          )}
          <div className="space-y-1">
            {topDepots.map((d, i) => (
              <button
                key={i}
                onClick={() => setDepotIdFilter(String(d.depot_id ?? ''))}
                className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/40 text-left"
              >
                <div className="text-sm text-gray-900 dark:text-gray-100">Dépôt #{d.depot_id}</div>
                <div className="shrink-0 px-2 py-0.5 rounded text-xs font-semibold bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200">
                  {d.nb_telechargements}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
        <div className="p-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Clock size={16} className="text-amber-500" />
            <div className="font-semibold text-gray-900 dark:text-gray-100">Journal des téléchargements</div>
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {data ? `${items.length} sur ${total}` : ''}
          </div>
        </div>

        {items.length === 0 && (
          <div className="p-6 text-sm text-gray-500 dark:text-gray-400 text-center">
            Aucun téléchargement sur cette période. {' '}
            <span className="text-xs">
              (le log ne remonte pas au-delà du 2026-08-27, date d'activation du traçage.)
            </span>
          </div>
        )}

        <ul className="divide-y divide-gray-100 dark:divide-gray-700">
          {items.map((l) => <LigneLog
            key={l.id}
            ligne={l}
            night={isNightRow(l.date)}
            mobile={badgeMobile(l.user_agent)}
            parsedDetail={parseDetail(l.detail)}
            detailOpen={!!detailOpen[l.id]}
            uaOpen={!!uaOpen[l.id]}
            onToggleDetail={() => setDetailOpen(s => ({ ...s, [l.id]: !s[l.id] }))}
            onToggleUa={() => setUaOpen(s => ({ ...s, [l.id]: !s[l.id] }))}
          />)}
        </ul>
      </div>
    </div>
  );
};

// ─── Composants internes ──────────────────────────────────────────────────

function KpiCard({ label, value, tone, hint }: { label: string; value: number; tone: 'neutral' | 'warn'; hint?: string }) {
  return (
    <div className={`rounded-xl border p-4 ${
      tone === 'warn'
        ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-800'
        : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
    }`}>
      <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
      <div className="mt-1 text-2xl font-bold text-gray-900 dark:text-gray-100">{value.toLocaleString()}</div>
      {hint && <div className="text-xs text-amber-700 dark:text-amber-300 mt-1">{hint}</div>}
    </div>
  );
}

function FilterField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">{label}</label>
      {children}
    </div>
  );
}

function LigneLog({ ligne, night, mobile, parsedDetail, detailOpen, uaOpen, onToggleDetail, onToggleUa }: {
  ligne: AuditTelechargementLigne;
  night: boolean;
  mobile: string | null;
  parsedDetail: Record<string, any> | null;
  detailOpen: boolean;
  uaOpen: boolean;
  onToggleDetail: () => void;
  onToggleUa: () => void;
}) {
  const d = new Date(ligne.date);
  const dateStr = d.toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'medium' });
  const matiere = parsedDetail?.matiere_libelle || parsedDetail?.matiere || '—';
  const enseignant = parsedDetail?.enseignant_nom || parsedDetail?.formateur || '—';
  const statut = parsedDetail?.statut_avant || parsedDetail?.statut || '';

  return (
    <li className={`p-3 md:p-4 ${night ? 'bg-amber-50/50 dark:bg-amber-900/10' : ''}`}>
      <div className="flex items-start gap-3 flex-wrap md:flex-nowrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {ligne.user_nom}
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {ligne.user_username} · {ligne.user_role}
            </span>
            {night && (
              <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                🌙 nuit
              </span>
            )}
            {mobile && (
              <span className="text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200">
                📱 {mobile}
              </span>
            )}
          </div>
          <div className="mt-1 text-xs text-gray-600 dark:text-gray-400 flex items-center gap-3 flex-wrap">
            <span className="inline-flex items-center gap-1">
              <Clock size={12} /> {dateStr}
            </span>
            <span className="inline-flex items-center gap-1">
              <Globe size={12} /> {ligne.ip || '—'}
            </span>
            <span>dépôt #{ligne.depot_id ?? '—'}</span>
            {statut && <span className="italic">statut: {statut}</span>}
          </div>
          <div className="mt-1 text-sm text-gray-700 dark:text-gray-300">
            <strong>{matiere}</strong>
            {enseignant !== '—' && <span className="text-xs text-gray-500 dark:text-gray-400"> — {enseignant}</span>}
          </div>

          <div className="mt-2 flex gap-4 text-xs">
            <button onClick={onToggleDetail} className="text-amber-600 dark:text-amber-400 hover:underline inline-flex items-center gap-1">
              <ChevronDown size={12} className={`transition-transform ${detailOpen ? 'rotate-180' : ''}`} />
              détail JSON
            </button>
            <button onClick={onToggleUa} className="text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center gap-1">
              <ChevronDown size={12} className={`transition-transform ${uaOpen ? 'rotate-180' : ''}`} />
              user-agent
            </button>
          </div>

          {detailOpen && (
            <pre className="mt-2 text-xs bg-gray-50 dark:bg-gray-900 p-2 rounded border border-gray-200 dark:border-gray-700 overflow-x-auto max-w-full whitespace-pre-wrap break-words">
              {parsedDetail ? JSON.stringify(parsedDetail, null, 2) : (ligne.detail || '—')}
            </pre>
          )}
          {uaOpen && (
            <pre className="mt-2 text-xs bg-gray-50 dark:bg-gray-900 p-2 rounded border border-gray-200 dark:border-gray-700 overflow-x-auto max-w-full whitespace-pre-wrap break-words">
              {ligne.user_agent || '—'}
            </pre>
          )}
        </div>
      </div>
    </li>
  );
}

export default AuditSujets;
